import React, { useState, useEffect } from 'react';
import { animated, useSpring, config, useTrail } from 'react-spring';
import { FiDollarSign, FiCalendar } from 'react-icons/fi';
import AnimatedCounter from './AnimatedCounter';
import ParticlesBackground from './ParticlesBackground';
import TypewriterValue from './TypewriterValue';
import ConfettiEffect from './ConfettiEffect';

interface MonthlyHighlightCardProps {
  totalInteractions: number;
  monetaryValue: number;
  isLoading?: boolean;
  monthStart?: string;
  monthEnd?: string;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
  }).format(value);
};

const MonthlyHighlightCard: React.FC<MonthlyHighlightCardProps> = ({
  totalInteractions,
  monetaryValue,
  isLoading = false,
  monthStart,
  monthEnd,
}) => {
  const [showShine, setShowShine] = useState(false);
  const [hover, setHover] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  useEffect(() => {
    // Efeito de brilho a cada 5 segundos
    const interval = setInterval(() => {
      setShowShine(true);
      setTimeout(() => setShowShine(false), 1500);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  // Ativar confetti quando o valor for alto
  useEffect(() => {
    if (totalInteractions > 90000 && !isLoading) {
      setShowConfetti(true);
      
      // Desativar após 5 segundos
      const timer = setTimeout(() => {
        setShowConfetti(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [totalInteractions, isLoading]);

  // Animação de entrada
  const cardAnimation = useSpring({
    from: { opacity: 0, transform: 'scale(0.9)' },
    to: { opacity: 1, transform: 'scale(1)' },
    config: { tension: 120, friction: 14 },
  });

  // Animação de hover
  const hoverAnimation = useSpring({
    transform: hover ? 'translateY(-5px)' : 'translateY(0px)',
    boxShadow: hover 
      ? '0 20px 25px -5px rgba(0, 0, 0, 0.2), 0 10px 10px -5px rgba(0, 0, 0, 0.1)' 
      : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    config: config.gentle
  });

  // Animação de brilho
  const shineAnimation = useSpring({
    opacity: showShine ? 1 : 0,
    transform: showShine ? 'translateX(100%)' : 'translateX(-100%)',
    config: { duration: 1500 },
  });

  // Animação para os elementos de texto
  const textItems = ['month', 'title', 'value', 'estimate', 'perInteraction'];
  const trail = useTrail(textItems.length, {
    from: { opacity: 0, transform: 'translateY(10px)' },
    to: { opacity: 1, transform: 'translateY(0px)' },
    config: { mass: 1, tension: 280, friction: 20 },
    delay: 200,
  });

  // Formatação do período
  const formatPeriod = () => {
    if (!monthStart || !monthEnd) return 'Mês atual';
    
    const startDate = new Date(monthStart);
    
    const monthNames = [
      'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
      'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
    ];
    
    return `${monthNames[startDate.getMonth()]} de ${startDate.getFullYear()}`;
  };

  // Ícones renderizados como SVG para evitar problemas de tipagem
  const CalendarIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      className="h-5 w-5 text-white/70" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" 
      />
    </svg>
  );

  const DollarIcon = () => (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      className="h-5 w-5 mr-1" 
      fill="none" 
      viewBox="0 0 24 24" 
      stroke="currentColor"
    >
      <path 
        strokeLinecap="round" 
        strokeLinejoin="round" 
        strokeWidth={2} 
        d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" 
      />
    </svg>
  );

  return (
    <animated.div 
      style={{...cardAnimation}} 
      className="relative overflow-hidden"
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      {/* Efeito de confetti para valores altos */}
      <ConfettiEffect 
        trigger={showConfetti} 
        colors={['#4ade80', '#22c55e', '#16a34a', '#15803d', '#166534']}
      />
      
      <animated.div 
        style={hoverAnimation} 
        className="bg-gradient-to-br from-emerald-500 to-green-600 rounded-xl p-6 shadow-lg text-white relative overflow-hidden transition-all duration-300 border-glow card-interactions"
      >
        {/* Efeito de brilho animado */}
        <animated.div 
          style={shineAnimation} 
          className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none"
        />
        
        {/* Fundo de partículas */}
        <ParticlesBackground 
          color="rgba(255, 255, 255, 0.5)"
          count={20}
          speed={0.3}
          opacity={0.2}
        />
        
        {/* Partículas decorativas */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
          {[...Array(6)].map((_, i) => (
            <div 
              key={i}
              className="absolute rounded-full bg-white/10"
              style={{
                width: `${Math.random() * 30 + 10}px`,
                height: `${Math.random() * 30 + 10}px`,
                top: `${Math.random() * 100}%`,
                left: `${Math.random() * 100}%`,
                animation: `float ${Math.random() * 10 + 10}s ease-in-out infinite`,
                animationDelay: `${Math.random() * 5}s`
              }}
            />
          ))}
        </div>
        
        <div className="flex items-center justify-between relative z-10">
          <div className="flex-1">
            <animated.div style={trail[0]} className="flex items-center space-x-2 mb-2">
              <CalendarIcon />
              <h3 className="text-md font-medium text-white/90 tracking-wide">{formatPeriod()}</h3>
            </animated.div>
            
            <animated.h2 style={trail[1]} className="text-xl font-bold mb-1 tracking-tight text-shadow">
              Total de Interações
            </animated.h2>
            
            <animated.div style={trail[2]} className="flex items-baseline space-x-2">
              <div className="text-5xl font-extrabold tracking-tight text-shadow glow">
                {isLoading ? (
                  <div className="animate-pulse bg-white/30 h-10 w-32 rounded"></div>
                ) : (
                  <AnimatedCounter 
                    value={totalInteractions} 
                    formatter={(value) => new Intl.NumberFormat('pt-BR').format(value)}
                    duration={2000}
                    animationType="gentle"
                    highlightChange={true}
                  />
                )}
              </div>
            </animated.div>
            
            <animated.div style={trail[3]} className="mt-4 flex items-center text-white/90">
              <DollarIcon />
              <span className="font-semibold">Valor estimado:</span>
              <span className="ml-2 text-xl font-bold text-shadow-glow">
                {isLoading ? (
                  <div className="animate-pulse bg-white/30 h-6 w-24 rounded"></div>
                ) : (
                  <TypewriterValue
                    value={monetaryValue}
                    prefix="R$ "
                    duration={2000}
                    delay={500}
                    formatter={(val) => val.toFixed(2).replace('.', ',')}
                    className="text-xl font-bold"
                  />
                )}
              </span>
            </animated.div>
            
            <animated.div style={trail[4]} className="mt-2 text-sm text-white/70 flex items-center">
              <span className="inline-flex items-center hover-bright transition-all">
                Valor por interação: 
                <span className="ml-1 font-medium">R$ 0,39</span>
              </span>
            </animated.div>
          </div>
          
          <animated.div 
            style={{
              ...trail[2],
              transform: hover ? 'scale(1.1) rotate(5deg)' : 'scale(1) rotate(0deg)',
              transition: 'transform 0.3s ease-out'
            }}
            className="hidden md:flex items-center justify-center w-28 h-28 bg-white/20 backdrop-blur-sm rounded-full shadow-inner hover-glow"
          >
            <svg 
              className="w-14 h-14 text-white drop-shadow-lg"
              xmlns="http://www.w3.org/2000/svg"
              fill="none" 
              viewBox="0 0 24 24" 
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </animated.div>
        </div>
        
        {/* Decorative elements */}
        <div className="absolute bottom-0 right-0 transform translate-x-4 translate-y-4">
          <svg width="120" height="120" viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="60" cy="60" r="60" fill="white" fillOpacity="0.05" />
            <circle cx="60" cy="60" r="40" fill="white" fillOpacity="0.05" />
            <circle cx="60" cy="60" r="20" fill="white" fillOpacity="0.05" />
          </svg>
        </div>
      </animated.div>
    </animated.div>
  );
};

export default MonthlyHighlightCard; 